import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Modal from "react-modal";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import PaymentLinkTable from "../../components/payment-link-system/PaymentLinkTable";
import ReactVortoxSpinner from "../../components/common-components/ReactVortoxSpinner";
import { convertStraight } from "../../components/common-data/Converts";
import ModalHeadAndClose from "../../components/payouts-wtf/common/ModalHeadAndClose";
import { showConfirmAlert } from "../../components/components-react/ReactAlert";
import { markStaffOffboardingComplete } from "../../redux/prospect-reducers/prospectApis";
import StaffCalendarViewReport from "../../components/trainer-report/staff-attendance-report/new-ui/StaffCalendarViewReport";

/**
 * Staff Off Boarding tab — lists F&F (Full & Final) offboarding records.
 * Admins can "Mark Completed" a pending settlement. The Attendance column
 * opens the existing StaffCalendarViewReport for the staff.
 */
const StaffOffBoardingTab = ({
  isPage,
  setIsPage,
  pageLimit,
  setPageLimit,
  userInfoData,
  callFunction,
}) => {
  const { staffOffboardingLoading, staffOffboardingError, staffOffboarding } =
    useSelector((state) => state.prospects);

  const isAdmin = userInfoData?.account_type === "admin";

  const iniDatas = { pagination: [], allData: [] };
  const [allDatas, setAllDatas] = useState(iniDatas);

  const iniModValues = { isModOpen: false, rowData: "", cardName: "" };
  const [modValues, setModValues] = useState(iniModValues);
  const [marking, setMarking] = useState(false);

  function onCloseModal() {
    setModValues(iniModValues);
  }

  useEffect(() => {
    if (
      !staffOffboardingLoading &&
      !staffOffboardingError &&
      staffOffboarding?.status &&
      staffOffboarding?.data?.length > 0
    ) {
      setAllDatas({
        allData: staffOffboarding.data,
        pagination:
          staffOffboarding?.pagination?.length > 0
            ? staffOffboarding.pagination
            : [],
      });
    } else {
      setAllDatas(iniDatas);
    }
  }, [staffOffboardingLoading, staffOffboardingError, staffOffboarding]);

  const fmtDateTime = (epochMs) => {
    if (!epochMs) return "";
    const n = Number(epochMs);
    if (Number.isNaN(n)) return epochMs;
    return moment(n).tz("Asia/Kolkata").format("DD-MM-YYYY hh:mm A");
  };

  const handleMarkCompleted = (row) => {
    const onConfirm = async () => {
      try {
        setMarking(true);
        // Only flip status + record who completed it. The backend stamps
        // fnf_done_date = today and preserves last_working_date / fnf_initiate_date.
        const payload = {
          fnf_status: "completed",
          offboarded_done_by: userInfoData?.uid || null,
        };
        const res = await markStaffOffboardingComplete(
          row?.id,
          payload,
          userInfoData?.token
        );
        if (res?.status) {
          toast.success(res?.message || "Marked as completed");
          if (callFunction) callFunction();
        } else {
          toast.error(res?.message || "Could not mark completed");
        }
      } catch (error) {
        toast.error("Something went wrong");
      } finally {
        setMarking(false);
      }
    };

    showConfirmAlert(
      onConfirm,
      "Are you sure you want to mark this F&F settlement as completed?",
      "Mark F&F Completed",
      "Mark Completed"
    );
  };

  const tbColumns = [
    {
      dataField: "uid",
      text: "Staff Details",
      formatter: (_, row) => (
        <div className="multi-row-table-td minWidth200">
          <small
            className="tb-data mid-padding mid-radius"
            style={{ background: "#F64E60", color: "#fff", letterSpacing: "1px" }}
          >
            {row?.user_details?.account_type?.replace(/_/g, " ") || "-"}
          </small>
          <span className="tb-data text-start">
            {row?.user_details?.name || "NA"}
          </span>
          <div
            className="highlighted-dotted-line"
            style={{ border: "1px solid gray", borderStyle: "dotted" }}
          ></div>
          <small className="tb-data text-start text-initial">
            {row?.user_details?.mobile || ""}
          </small>
          <small className="tb-data text-start text-initial text-lowercase">
            {row?.user_details?.email || ""}
          </small>
        </div>
      ),
    },
    {
      dataField: "fnf_initiate_date",
      text: "Attendance Period",
      formatter: (_, row) => (
        <div className="tb-data text-center minWidth150">
          {row?.fnf_initiate_date ? convertStraight(row.fnf_initiate_date) : "-"}
          <span style={{ margin: "0 4px", color: "#888" }}>to</span>
          {row?.fnf_done_date ? convertStraight(row.fnf_done_date) : "-"}
        </div>
      ),
    },
    {
      dataField: "attendance",
      text: "Attendance",
      formatter: (_, row) => (
        <div className="tb-data text-center">
          <button
            className="actionBtn icon view"
            title="View Attendance"
            onClick={() =>
              setModValues({
                isModOpen: true,
                cardName: "attendance",
                rowData: row,
              })
            }
          >
            View
          </button>
        </div>
      ),
    },
    {
      dataField: "fnf_status",
      text: "FNF Status",
      formatter: (_, row) => {
        const isCompleted = row?.fnf_status === "completed";
        return (
          <span
            className="tb-data mid-padding mid-radius"
            style={{
              background: isCompleted ? "#1BC5BD" : "#FFA800",
              color: "#fff",
              textTransform: "capitalize",
            }}
          >
            {row?.fnf_status || "pending"}
          </span>
        );
      },
    },
    {
      dataField: "offboarded_by",
      text: "Offboard By",
      formatter: (_, row) => (
        <div className="tb-data text-center">
          {row?.offboarded_by_name || row?.offboarded_by || "-"}
        </div>
      ),
    },
    {
      dataField: "offboarded_done_by",
      text: "Offboard Done By",
      formatter: (_, row) => (
        <div className="tb-data text-center">
          {row?.offboarded_done_by_name || row?.offboarded_done_by || "-"}
        </div>
      ),
    },
    {
      dataField: "date_added",
      text: "Date Added",
      formatter: (_, row) => (
        <div className="tb-data text-center minWidth100">
          {fmtDateTime(row?.date_added)}
        </div>
      ),
    },
    {
      dataField: "last_updated",
      text: "Last Update",
      formatter: (_, row) => (
        <div className="tb-data text-center minWidth100">
          {fmtDateTime(row?.last_updated)}
        </div>
      ),
    },
    {
      dataField: "action",
      text: "Action",
      formatter: (_, row) => {
        const isPending = (row?.fnf_status || "pending") === "pending";
        if (isAdmin && isPending) {
          return (
            <div className="tb-data text-center">
              <button
                className="actionBtn"
                disabled={marking}
                onClick={() => handleMarkCompleted(row)}
              >
                Mark Completed
              </button>
            </div>
          );
        }
        return (
          <span
            className="tb-data mid-padding mid-radius"
            style={{ background: "#1BC5BD", color: "#fff" }}
          >
            Completed
          </span>
        );
      },
    },
  ];

  return (
    <>
      <Modal
        isOpen={modValues.isModOpen}
        onRequestClose={onCloseModal}
        contentLabel="Attendance Modal"
        ariaHideApp={false}
      >
        <div className="createNewCTOModalContainer">
          {modValues.cardName === "attendance" && (
            <>
              <ModalHeadAndClose
                title={`${
                  modValues.rowData?.user_details?.name || "Staff"
                } — Attendance`}
                onClose={onCloseModal}
              />
              <StaffCalendarViewReport
                userInfoData={userInfoData}
                rowData={{ user_id: modValues.rowData?.user_id }}
              />
            </>
          )}
        </div>
      </Modal>

      {staffOffboardingLoading ? (
        <ReactVortoxSpinner />
      ) : (
        <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table">
          <PaymentLinkTable
            tableData={allDatas.allData}
            tableColumns={tbColumns}
            tablePagination={allDatas.pagination}
            isPage={isPage}
            setIsPage={(value) => setIsPage(value)}
            pageLimit={pageLimit}
            setPageLimit={setPageLimit}
          />
        </div>
      )}
    </>
  );
};

export default StaffOffBoardingTab;
